import {
  definePreset,
  DEGREES_SCHEMA,
  DIRECTION_SCHEMA,
  EDGE_DIRECTION_SCHEMA,
  DISTANCE_SCHEMA,
  kf,
  rad,
  samplePath,
  setStatic,
  SLIDE_VECTORS,
  timing,
  WIPE_TO_REVEAL_DIR,
} from "./helpers";

import type { AnimationPreset } from "./types";

const FRAGMENT_SCHEMA = [
  { key: "pieces", label: "Pieces", min: 2, max: 10, step: 1 },
  { key: "spread", label: "Spread", min: 0.2, max: 3, step: 0.1 },
  { key: "gravity", label: "Gravity", min: 0, max: 3, step: 0.1 },
  { key: "twist", label: "Twist", min: 0, max: 3, step: 0.1 },
];

const FRAGMENT_DEFAULTS = { pieces: 6, spread: 1, gravity: 1, twist: 1 };

/** Shared explode/assemble/shatter build — direction picks which way the
 * flight runs (explode 0→1, assemble 1→0); shards splits cells into
 * triangles. */
export const fragmentEffect =
  (from: number, to: number, shards = false): AnimationPreset["build"] =>
  (tl, target, _element, cfg) => {
    setStatic(tl, target, {
      fragCount: Math.round(cfg.params.pieces ?? 6),
      fragSpread: cfg.params.spread ?? 1,
      fragGravity: cfg.params.gravity ?? 1,
      fragTwist: cfg.params.twist ?? 1,
      fragShards: shards ? 1 : 0,
    });
    tl.fromTo(
      target,
      { fragProgress: from },
      { fragProgress: to, ...timing(cfg) },
      0,
    );
  };

export const exitPresets: AnimationPreset[] = [
  definePreset(
    "explode",
    "Explode",
    "exit",
    {
      params: { ...FRAGMENT_DEFAULTS },
      schema: FRAGMENT_SCHEMA,
      duration: 1200,
      easing: "power2.in",
    },
    fragmentEffect(0, 1),
  ),
  definePreset(
    "shatter",
    "Shatter",
    "exit",
    {
      params: { ...FRAGMENT_DEFAULTS, pieces: 5, twist: 1.5 },
      schema: FRAGMENT_SCHEMA,
      duration: 1200,
      easing: "power2.in",
    },
    // triangular shards read as broken glass rather than a mosaic
    fragmentEffect(0, 1, true),
  ),
  definePreset(
    "fadeOut",
    "Fade Out",
    "exit",
    {
      params: { to: 0 },
      schema: [{ key: "to", label: "To %", min: 0, max: 100, step: 5 }],
    },
    (tl, target, _element, cfg) => {
      tl.to(target, { opacity: (cfg.params.to ?? 0) / 100, ...timing(cfg) }, 0);
    },
  ),
  definePreset(
    "slideOut",
    "Slide Out",
    "exit",
    {
      params: { direction: 1, distance: 200 },
      schema: [DIRECTION_SCHEMA, DISTANCE_SCHEMA],
      easing: "power2.in",
    },
    (tl, target, _element, cfg) => {
      const [vx, vy] =
        SLIDE_VECTORS[cfg.params.direction ?? 1] ?? SLIDE_VECTORS[1];
      const d = cfg.params.distance ?? 200;
      tl.to(target, { dx: vx * d, dy: vy * d, opacity: 0, ...timing(cfg) }, 0);
    },
  ),
  definePreset(
    "zoomOut",
    "Zoom Out",
    "exit",
    {
      params: { to: 0 },
      schema: [{ key: "to", label: "To scale", min: 0, max: 1, step: 0.05 }],
      easing: "power2.in",
    },
    (tl, target, _element, cfg) => {
      const to = cfg.params.to ?? 0;
      tl.to(target, { scaleX: to, scaleY: to, opacity: 0, ...timing(cfg) }, 0);
    },
  ),
  definePreset(
    "bounceOut",
    "Bounce Out",
    "exit",
    {
      params: { to: 0.3, intensity: 1 },
      schema: [
        { key: "to", label: "To scale", min: 0, max: 1, step: 0.05 },
        { key: "intensity", label: "Overshoot", min: 0.1, max: 3, step: 0.1 },
      ],
    },
    (tl, target, _element, cfg) => {
      const to = cfg.params.to ?? 0.3;
      const k = cfg.params.intensity ?? 1;
      const scale = [1, ...[0.95, 1.1, 1.1].map((v) => 1 + (v - 1) * k), to];
      tl.to(
        target,
        kf(
          cfg,
          { scaleX: scale, scaleY: scale, opacity: [1, 1, 1, 1, 0] },
          "sine.in",
        ),
        0,
      );
    },
  ),
  definePreset(
    "rotateOut",
    "Rotate Out",
    "exit",
    {
      params: { degrees: 180, scale: 0.5 },
      schema: [
        DEGREES_SCHEMA,
        { key: "scale", label: "To scale", min: 0, max: 1, step: 0.05 },
      ],
      easing: "power2.in",
    },
    (tl, target, _element, cfg) => {
      const to = cfg.params.scale ?? 0.5;
      tl.to(
        target,
        {
          angleDelta: rad(cfg.params.degrees ?? 180),
          scaleX: to,
          scaleY: to,
          opacity: 0,
          ...timing(cfg),
        },
        0,
      );
    },
  ),
  definePreset(
    "flipOutX",
    "Flip Out X",
    "exit",
    {
      params: { to: 0 },
      schema: [{ key: "to", label: "To scale", min: 0, max: 1, step: 0.05 }],
      easing: "power2.in",
    },
    (tl, target, _element, cfg) => {
      tl.to(
        target,
        { scaleY: cfg.params.to ?? 0, opacity: 0, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "flipOutY",
    "Flip Out Y",
    "exit",
    {
      params: { to: 0 },
      schema: [{ key: "to", label: "To scale", min: 0, max: 1, step: 0.05 }],
      easing: "power2.in",
    },
    (tl, target, _element, cfg) => {
      tl.to(
        target,
        { scaleX: cfg.params.to ?? 0, opacity: 0, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "rollOut",
    "Roll Out",
    "exit",
    {
      params: { distance: 200, degrees: 120 },
      schema: [
        DISTANCE_SCHEMA,
        { key: "degrees", label: "Roll °", min: 10, max: 360, step: 5 },
      ],
      easing: "power2.in",
    },
    (tl, target, _element, cfg) => {
      tl.to(
        target,
        {
          dx: cfg.params.distance ?? 200,
          angleDelta: rad(cfg.params.degrees ?? 120),
          opacity: 0,
          ...timing(cfg),
        },
        0,
      );
    },
  ),
  definePreset(
    "wipeOut",
    "Wipe Out",
    "exit",
    {
      params: { direction: 1 },
      schema: [EDGE_DIRECTION_SCHEMA],
      easing: "none",
    },
    (tl, target, _element, cfg) => {
      setStatic(tl, target, {
        revealDir:
          WIPE_TO_REVEAL_DIR[cfg.params.direction ?? 1] ??
          WIPE_TO_REVEAL_DIR[1],
      });
      tl.fromTo(
        target,
        { revealProgress: 1 },
        { revealProgress: 0, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "irisOut",
    "Iris Out",
    "exit",
    { easing: "power1.inOut" },
    // radial wipe out: the circle shrinks to nothing at the center
    (tl, target, _element, cfg) => {
      setStatic(tl, target, { revealDir: 4 });
      tl.fromTo(
        target,
        { revealProgress: 1 },
        { revealProgress: 0, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "blurOut",
    "Blur Out",
    "exit",
    {
      params: { blur: 12 },
      schema: [{ key: "blur", label: "Blur px", min: 1, max: 50, step: 1 }],
      easing: "power2.in",
    },
    (tl, target, _element, cfg) => {
      tl.to(
        target,
        { blur: cfg.params.blur ?? 12, opacity: 0, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "hinge",
    "Hinge",
    "exit",
    {
      params: { distance: 500 },
      schema: [DISTANCE_SCHEMA],
      duration: 1800,
    },
    (tl, target, _element, cfg) => {
      const d = cfg.params.distance ?? 500;
      tl.to(
        target,
        kf(
          cfg,
          {
            angleDelta: [0, 70, 55, 70, 55, 90].map(rad),
            dy: [0, 0, 0, 0, 0, d],
            opacity: [1, 1, 1, 1, 1, 0],
          },
          "sine.inOut",
        ),
        0,
      );
    },
  ),
  definePreset(
    "spiralOut",
    "Spiral Out",
    "exit",
    {
      params: { distance: 200, turns: 1, scale: 0.4 },
      schema: [
        DISTANCE_SCHEMA,
        { key: "turns", label: "Turns", min: 0.5, max: 3, step: 0.5 },
        { key: "scale", label: "To scale", min: 0, max: 1, step: 0.05 },
      ],
      duration: 1400,
      easing: "power2.in",
    },
    (tl, target, _element, cfg) => {
      const r0 = cfg.params.distance ?? 200;
      const turns = cfg.params.turns ?? 1;
      const to = cfg.params.scale ?? 0.4;
      const { dx, dy } = samplePath((t) => {
        const r = r0 * t;
        const a = turns * 2 * Math.PI * t;
        return [r * Math.cos(a), r * Math.sin(a)];
      });
      tl.to(
        target,
        kf(cfg, {
          dx,
          dy,
          opacity: [1, 0],
          scaleX: [1, to],
          scaleY: [1, to],
        }),
        0,
      );
    },
  ),
  definePreset(
    "vanish",
    "Vanish",
    "exit",
    {
      params: { blur: 16, grow: 1.2 },
      schema: [
        { key: "blur", label: "Blur px", min: 1, max: 50, step: 1 },
        { key: "grow", label: "Grow to", min: 1, max: 3, step: 0.05 },
      ],
      easing: "power2.in",
    },
    (tl, target, _element, cfg) => {
      const grow = cfg.params.grow ?? 1.2;
      tl.to(
        target,
        {
          blur: cfg.params.blur ?? 16,
          scaleX: grow,
          scaleY: grow,
          opacity: 0,
          ...timing(cfg),
        },
        0,
      );
    },
  ),
  definePreset(
    "elasticOut",
    "Elastic Out",
    "exit",
    {
      params: { to: 0.2 },
      schema: [{ key: "to", label: "To scale", min: 0, max: 1, step: 0.05 }],
      duration: 1200,
      easing: "elastic.in",
    },
    (tl, target, _element, cfg) => {
      const to = cfg.params.to ?? 0.2;
      tl.to(target, { scaleX: to, scaleY: to, opacity: 0, ...timing(cfg) }, 0);
    },
  ),
  definePreset(
    "backOut",
    "Back Out",
    "exit",
    {
      params: { to: 0.4 },
      schema: [{ key: "to", label: "To scale", min: 0, max: 1, step: 0.05 }],
      easing: "back.in",
    },
    // mirror of backIn: anticipation overshoot, then shrink away
    (tl, target, _element, cfg) => {
      const to = cfg.params.to ?? 0.4;
      tl.to(target, { scaleX: to, scaleY: to, opacity: 0, ...timing(cfg) }, 0);
    },
  ),
  definePreset(
    "swingOut",
    "Swing Out",
    "exit",
    {
      params: { degrees: 60 },
      schema: [
        { key: "degrees", label: "Degrees", min: 10, max: 120, step: 5 },
      ],
      duration: 1200,
    },
    // mirror of swingIn: small counter-swings build into the big drop-off
    (tl, target, _element, cfg) => {
      const d = cfg.params.degrees ?? 60;
      tl.to(
        target,
        kf(
          cfg,
          {
            angleDelta: [0, d * 0.05, -d * 0.15, d * 0.3, -d].map(rad),
            opacity: [1, 1, 1, 1, 0],
          },
          "sine.in",
        ),
        0,
      );
    },
  ),
  definePreset(
    "sketchOut",
    "Sketch Out",
    "exit",
    {
      params: { passes: 3, jitter: 1 },
      schema: [
        { key: "passes", label: "Passes", min: 1, max: 4, step: 1 },
        { key: "jitter", label: "Jitter", min: 0, max: 2, step: 0.1 },
      ],
      duration: 1400,
      easing: "power1.in",
    },
    // mirror of sketchIn: the stroke un-draws while roughness builds and the
    // rough seed re-rolls once per pass — the drawing scribbles itself away
    (tl, target, _element, cfg) => {
      const passes = Math.max(1, Math.round(cfg.params.passes ?? 3));
      const jitter = cfg.params.jitter ?? 1;
      tl.fromTo(
        target,
        { drawProgress: 1, opacity: 1 },
        { drawProgress: 0, opacity: 0.3, ...timing(cfg) },
        0,
      );
      tl.fromTo(
        target,
        { roughnessDelta: 0 },
        { roughnessDelta: jitter * 2, ...timing(cfg) },
        0,
      );
      // linear on purpose — matches sketchIn's equal-length seed passes
      tl.fromTo(
        target,
        { seedShift: 1 },
        { seedShift: passes + 1 - 1e-6, ...timing(cfg), ease: "none" },
        0,
      );
    },
  ),
  definePreset(
    "lightSpeedOut",
    "Light Speed Out",
    "exit",
    {
      params: { direction: 1, distance: 300, degrees: 25 },
      schema: [
        {
          key: "direction",
          label: "To side",
          min: 0,
          max: 1,
          step: 1,
          options: ["Left", "Right"],
        },
        { key: "distance", label: "Distance px", min: 50, max: 1000, step: 10 },
        { key: "degrees", label: "Lean °", min: 5, max: 60, step: 1 },
      ],
      duration: 700,
      easing: "power2.in",
    },
    // the exit half of the light-speed pair: leans into the motion and
    // streaks off, fading as it goes
    (tl, target, _element, cfg) => {
      const sign = (cfg.params.direction ?? 1) === 0 ? -1 : 1;
      tl.to(
        target,
        {
          dx: (cfg.params.distance ?? 300) * sign,
          skewX: rad(cfg.params.degrees ?? 25) * sign,
          opacity: 0,
          ...timing(cfg),
        },
        0,
      );
    },
  ),
  definePreset(
    "peekOut",
    "Peek Out",
    "exit",
    {
      params: { direction: 3, distance: 80 },
      schema: [
        EDGE_DIRECTION_SCHEMA,
        { key: "distance", label: "Distance px", min: 10, max: 500, step: 10 },
      ],
      duration: 800,
      easing: "power2.in",
    },
    // reverse peek: retreats toward the chosen edge while the reveal shrinks
    // toward it — slides back behind its mask (same 1:1 direction→revealDir
    // map as peekIn)
    (tl, target, _element, cfg) => {
      const direction = Math.abs(Math.round(cfg.params.direction ?? 3)) % 4;
      setStatic(tl, target, { revealDir: direction });
      const [vx, vy] = SLIDE_VECTORS[direction] ?? SLIDE_VECTORS[3];
      const d = cfg.params.distance ?? 80;
      tl.fromTo(
        target,
        { dx: 0, dy: 0, revealProgress: 1 },
        { dx: vx * d, dy: vy * d, revealProgress: 0, ...timing(cfg) },
        0,
      );
    },
  ),
];
